import {
  countByPhase,
  PROFILE_INCOMPLETE_CODE,
  type MiceColor,
  type MiceNextAction,
  type MiceProjectView,
  type MiceTraveler,
} from '@voll-intelligence/domain';
import { t } from '@web/i18n';
import { Button, EmptyHint, type DataTableSubRow } from '@web/ui/core';
import { fireAndForget } from '@web/ui/core/utils';
import { useProfileBulk } from '../hooks/use_profile_actions';
import { useApprovedNeeds, useTravelerActions } from '../hooks/use_traveler_actions';
import { useTravelerOverlays, type TravelerOverlayState } from '../hooks/use_traveler_overlays';
import { useTravelersView } from '../hooks/use_travelers_view';
import { eligibleIds, travelerProfileCheck, type MiceBulkAction } from '../travelers/eligibility';
import type { MiceTravelerFilter } from '../travelers/traveler_filter';
import { setupPending } from './event_setup_checklist';
import { EventSummaryDrawer } from './event_summary_drawer';
import { FiltersRail } from './filters_rail';
import { SelectionBar } from './selection_bar';
import { TravelerOverlays } from './traveler_overlays';
import { orderSubRows } from './traveler_tree';
import { TravelersTable } from './travelers_table';
import { TravelersStatusLine } from './travelers_status_line';
import { TravelersToolbar } from './travelers_toolbar';

interface TravelersPanelProps {
  projectId: string;
  canPurchase: boolean;
  /** Compras do evento abertas: encerradas pelo organizador, saem recomendar, comprar, enfileirar,
   *  tentar de novo e liberar (o servidor recusa); aprovar, tirar da fila e o cadastro seguem. */
  purchasesOpen: boolean;
  canManage: boolean;
  premisesReady: boolean;
  priceTolerance: number;
  /** O projeto: a linha de status e o painel Resumo leem a compra e as premissas dele. */
  project: MiceProjectView | undefined;
  /** Coluna de filtros aberta (o estado fica na página, que põe o botão no cabeçalho). */
  railOpen: boolean;
  /** O painel Resumo fica aqui, ao lado do filtro de fase que o "precisam de você" muda. */
  summaryOpen: boolean;
  onSummaryClose: () => void;
  /** Abre o painel de edição das premissas. */
  onPremises: () => void;
}

type Actions = ReturnType<typeof useTravelerActions>;

/** Com a checagem, o painel abre na seção Cadastro. Sem ela (validado antes), o motivo do pedido
 *  parado por cadastro mora no pedido; o da necessidade, no painel dela. */
function openProfileStep(tr: MiceTraveler, overlays: TravelerOverlayState): void {
  if (travelerProfileCheck(tr)) {
    overlays.openProfile(tr.id);
    return;
  }
  const blocked = tr.orders.find((o) => o.order.error?.code === PROFILE_INCOMPLETE_CODE);
  if (blocked) overlays.openOrder(blocked.order.id);
  else overlays.openTraveler(tr.id);
}

/** O que o botão da próxima ação faz em cada viajante. */
function nextActionHandler(actions: Actions, overlays: TravelerOverlayState) {
  return (tr: MiceTraveler): void => {
    const needIds = tr.need ? [tr.need.id] : [];
    switch (tr.nextAction) {
      case 'recommend':
        actions.recommend(needIds);
        return;
      case 'approve':
        actions.approve(needIds);
        return;
      case 'buy':
        // Só a aprovada vira pedido pela revisão; com pedido já criado (planilha, ou tirado da
        // fila), comprar é pôr esse pedido de volta na fila.
        if (tr.need?.status === 'approved') overlays.openPurchase([tr.need]);
        else actions.queue(eligibleIds('retry', [tr]));
        return;
      case 'retry':
        actions.queue(eligibleIds('retry', [tr]));
        return;
      case 'verify':
        overlays.openVerify(tr.id);
        return;
      case 'complete_profile':
        openProfileStep(tr, overlays);
        return;
      case 'reconsider':
      case 'review':
      case 'wait':
      case 'details':
      case 'none':
      default:
        overlays.openTraveler(tr.id);
    }
  };
}

/** As próximas ações que criam, recomendam ou põem compra na fila. */
const BUYING_NEXT: ReadonlySet<MiceNextAction> = new Set([
  'recommend',
  'reconsider',
  'buy',
  'retry',
  'verify',
]);

/** A próxima ação da linha desligada agora: em curso, recomendar sem premissas conferidas ou
 *  compra com as compras do evento encerradas. */
const blockedNext =
  (busy: boolean, premisesReady: boolean, purchasesOpen: boolean) =>
  (action: MiceNextAction): boolean =>
    busy ||
    (action === 'recommend' && !premisesReady) ||
    (!purchasesOpen && BUYING_NEXT.has(action));

/** Só os pedidos viram sub-linhas: o voucher de cada um abre o painel do pedido. */
const subRowsOf =
  (overlays: TravelerOverlayState) =>
  (tr: MiceTraveler): DataTableSubRow[] | null =>
    orderSubRows(tr, (order) => {
      overlays.openOrder(order.id);
    });

/**
 * "Emitir N verdes" leva todas as verdes do evento: só aparece em "Para emitir" e sem busca, alerta
 * ou origem, quando a contagem da tela é a do evento inteiro (a cor não muda a contagem).
 */
function eventGreens(filter: MiceTravelerFilter, colorCounts: Record<MiceColor, number>): number {
  const wholeEvent = filter.text.trim() === '' && filter.alert === 'any' && filter.origin === 'any';
  return filter.phase === 'approval' && wholeEvent ? colorCounts.green : 0;
}

/** O nome do viajante selecionado (ou o e-mail) para o aviso da decisão em lote. */
const nameIn =
  (selected: readonly MiceTraveler[]) =>
  (id: string): string => {
    const tr = selected.find((s) => s.id === id);
    return tr?.name ?? tr?.email ?? id;
  };

export function TravelersPanel(props: Readonly<TravelersPanelProps>) {
  const { projectId, canPurchase, purchasesOpen, project } = props;
  const actions = useTravelerActions(projectId);
  const view = useTravelersView(projectId);
  const approved = useApprovedNeeds(projectId);
  const profileBulk = useProfileBulk(projectId);
  const { travelers, isLoading, isError } = view;
  const overlays = useTravelerOverlays();

  const { all: travelerCount, attention } = project?.phaseCounts ?? countByPhase([]);
  // O checklist já pede as premissas; o aviso na linha de status é para quem não o vê.
  const checklistVisible = project ? setupPending(project, travelerCount, props.canManage) : false;

  const showAttention = (): void => {
    // Sem busca, origem ou alerta: a lista mostra todos os viajantes que o número contou.
    view.setFilter({ phase: 'attention', text: '', origin: 'any', alert: 'any' });
    props.onSummaryClose();
  };
  const reviewApproved = (): void => {
    fireAndForget(
      actions.fetchApproved().then((needs) => {
        if (needs.length > 0) overlays.openPurchase(needs);
      }),
    );
  };

  const onAction = (action: MiceBulkAction, ids: string[]): void => {
    if (action === 'buy') {
      overlays.openPurchase(
        view.selected.flatMap((tr) => (tr.need && ids.includes(tr.need.id) ? [tr.need] : [])),
      );
      return;
    }
    if (action === 'profile_sheet' || action === 'profile_keep') {
      const choice = action === 'profile_sheet' ? 'sheet_to_complete' : 'keep_voll';
      const nameOf = nameIn(view.selected);
      profileBulk.resolveBulk(ids, choice, { onDone: view.clearSelection, nameOf });
      return;
    }
    const run = {
      recommend: actions.recommend,
      approve: actions.approve,
      manual: actions.manual,
      retry: actions.queue,
      unqueue: actions.unqueue,
      remove: actions.remove,
    }[action];
    run(ids, view.clearSelection);
  };

  return (
    <>
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
        {props.railOpen ? (
          <FiltersRail
            counts={view.counts}
            colorCounts={view.colorCounts}
            filter={view.filter}
            origins={view.origins}
            onFilter={view.setFilter}
          />
        ) : null}
        <div className="flex min-w-0 flex-1 flex-col gap-4">
          <TravelersToolbar
            filter={view.filter}
            onFilter={view.setFilter}
            railOpen={props.railOpen}
            count={view.total}
            allOpen={view.allOpen}
            onToggleAll={view.toggleAll}
          />
          <TravelersStatusLine
            counts={project?.counts}
            needsYou={attention}
            approved={approved.length}
            green={eventGreens(view.filter, view.colorCounts)}
            canPurchase={canPurchase && purchasesOpen}
            premisesChecked={props.premisesReady || travelerCount === 0 || checklistVisible}
            onNeedsYou={showAttention}
            onReviewApproved={reviewApproved}
            onEmitGreen={actions.orderGreen}
            busy={actions.busy}
            onCheckPremises={props.onPremises}
          />
          {isLoading || isError ? (
            <EmptyHint>
              {t(isError ? 'mice.travelers.loadFailed' : 'mice.travelers.loading')}
            </EmptyHint>
          ) : (
            <TravelersTable
              travelers={travelers}
              onOpen={(tr) => {
                overlays.openTraveler(tr.id);
              }}
              expanded={view.expanded}
              onToggle={view.toggle}
              selected={view.selectedIds}
              selectState={view.selectState}
              onSelect={view.toggleSelected}
              onSelectAll={(on) => {
                fireAndForget(view.selectAll(on));
              }}
              canAct={canPurchase}
              blocked={blockedNext(actions.busy, props.premisesReady, purchasesOpen)}
              onAction={nextActionHandler(actions, overlays)}
              renderSubRows={subRowsOf(overlays)}
            />
          )}
          {view.moreCount > 0 ? (
            <div className="flex justify-center">
              <Button variant="outline" size="sm" onClick={view.loadMore}>
                {t('mice.travelers.more', { count: view.moreCount })}
              </Button>
            </div>
          ) : null}
        </div>
      </div>
      {project ? (
        <EventSummaryDrawer
          open={props.summaryOpen}
          onClose={props.onSummaryClose}
          project={project}
          onEditPremises={() => {
            props.onSummaryClose();
            props.onPremises();
          }}
          onShowAttention={showAttention}
        />
      ) : null}
      <SelectionBar
        selected={view.selected}
        canPurchase={canPurchase}
        purchasesOpen={purchasesOpen}
        canManage={props.canManage}
        premisesReady={props.premisesReady}
        busy={actions.busy || profileBulk.busy}
        onAction={onAction}
        onClear={view.clearSelection}
      />
      <TravelerOverlays
        projectId={projectId}
        canPurchase={canPurchase}
        canManage={props.canManage}
        project={project}
        purchasesOpen={purchasesOpen}
        priceTolerance={props.priceTolerance}
        travelers={travelers}
        sequence={travelers.filter((tr) => tr.need !== null)}
        overlays={overlays}
        actions={actions}
        onDone={view.clearSelection}
      />
    </>
  );
}
